import React, { useEffect, useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { useConfirm } from '../components/ConfirmDialog';

// Icon/logo upload — reuses hero-slides bucket (public read + admin
// write via RLS). Same MIME allow-list as other uploads on the panel.
const ALLOWED_ICON_TYPES: Record<string, { ext: string; contentType: string }> = {
  'image/jpeg': { ext: 'jpg', contentType: 'image/jpeg' },
  'image/png':  { ext: 'png', contentType: 'image/png'  },
  'image/webp': { ext: 'webp', contentType: 'image/webp' },
  'image/svg+xml': { ext: 'svg', contentType: 'image/svg+xml' },
};
const MAX_ICON_BYTES = 2 * 1024 * 1024;

async function uploadPaymentIcon(file: File): Promise<string> {
  const spec = ALLOWED_ICON_TYPES[file.type];
  if (!spec) throw new Error('نوع الملف مش مسموح — JPG/PNG/WebP/SVG فقط');
  if (file.size > MAX_ICON_BYTES) throw new Error('حجم الأيقونة أكبر من 2MB');
  // Batch 46 / SPECS/006: write under `payments/` so the per-prefix
  // storage policy `hero_slides_payments_write_*` gates this upload on
  // `has_section_access('payment_methods')` — a payment-methods-only
  // admin can save icons; a home-only admin cannot.
  const path = `payments/payment-icon-${crypto.randomUUID()}.${spec.ext}`;
  const { error: upErr } = await supabase.storage.from('hero-slides').upload(path, file, {
    cacheControl: '31536000',
    contentType: spec.contentType,
    upsert: false,
  });
  if (upErr) throw upErr;
  const { data } = supabase.storage.from('hero-slides').getPublicUrl(path);
  return data.publicUrl;
}

// Full CRUD for the "طرق التبرع" mobile tab. Rows are grouped into 3
// sections (delivery / electronic / bank); bank accounts nest under a
// bank parent via parent_id. Every field the mobile screen shows —
// label, note, value (phone/code/account #), currency, icon, tint —
// is editable here.

type Section = 'delivery' | 'electronic' | 'bank';
type Row = {
  id: string;
  section: Section;
  kind: string;
  label_ar: string;
  label_en: string;
  note_ar: string | null;
  note_en: string | null;
  value: string | null;
  currency: string | null;
  icon: string | null;
  logo_url: string | null;
  tint: string | null;
  parent_id: string | null;
  sort_order: number;
  active: boolean;
};

type Draft = Omit<Row, 'id'> & { id?: string };

const SECTION_LABEL: Record<Section, string> = {
  delivery: 'مندوب',
  electronic: 'إلكتروني',
  bank: 'بنوك',
};

const KIND_HINT: Record<string, string> = {
  call_center: 'رقم اتصال (مثلاً 19340)',
  fawry: 'كود فوري (مثلاً 9200)',
  wallet: 'محفظة إلكترونية بدون رقم للنسخ',
  sms: 'كود SMS + محتوى الرسالة ف "value"',
  bank: 'رأس مجموعة (البنك نفسه)',
  account: 'حساب بنكي داخل بنك (parent_id مطلوب)',
};

const emptyDraft: Draft = {
  section: 'delivery',
  kind: 'call_center',
  label_ar: '',
  label_en: '',
  note_ar: '',
  note_en: '',
  value: '',
  currency: '',
  icon: '',
  logo_url: '',
  tint: '#009091',
  parent_id: null,
  sort_order: 0,
  active: true,
};

export default function PaymentMethodsPage({ defaultTab = 'delivery' }: { defaultTab?: Section } = {}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Draft | null>(null);
  const confirm = useConfirm();
  const [creating, setCreating] = useState(false);
  // The tab is now driven by the URL (via defaultTab from the route
  // element). The internal state is kept so the same file renders
  // just one tab per route without redirecting.
  const tab = defaultTab;

  useEffect(() => {
    void load();
  }, []);

  const load = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('payment_methods')
      .select('*')
      .order('section')
      .order('sort_order');
    if (err) setError(err.message);
    else setRows((data ?? []) as Row[]);
    setLoading(false);
  };

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement | null>(null);

  // ==== Bank editor (create + edit) ====
  // Every bank interaction — add a new bank OR edit an existing one —
  // uses ONE clean form: bank name (ar/en), logo upload, and a list of
  // (currency, account number) rows the operator can add / remove.
  // The old single-row modal is not used for banks at all.
  type BankEditorAccount = { id?: string; currency: 'EGP' | 'USD' | 'EUR'; value: string };
  type BankEditor = {
    id?: string; // existing bank id in edit mode
    label_ar: string;
    label_en: string;
    logo_url: string;
    accounts: BankEditorAccount[];
  };
  const [bankEditor, setBankEditor] = useState<BankEditor | null>(null);
  const [savingBank, setSavingBank] = useState(false);
  const [uploadingBankLogo, setUploadingBankLogo] = useState(false);
  const bankLogoInputRef = React.useRef<HTMLInputElement | null>(null);

  const startNewBank = () => {
    setBankEditor({
      label_ar: '',
      label_en: '',
      logo_url: '',
      accounts: [{ currency: 'EGP', value: '' }],
    });
  };

  const startEditBank = (bank: Row) => {
    const accts = rows
      .filter((r) => r.parent_id === bank.id)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((a) => ({
        id: a.id,
        currency: (a.currency ?? 'EGP') as BankEditorAccount['currency'],
        value: a.value ?? '',
      }));
    setBankEditor({
      id: bank.id,
      label_ar: bank.label_ar,
      label_en: bank.label_en,
      logo_url: bank.logo_url ?? '',
      accounts: accts.length > 0 ? accts : [{ currency: 'EGP', value: '' }],
    });
  };

  const saveBankEditor = async () => {
    if (!bankEditor) return;
    if (!bankEditor.label_ar.trim()) {
      setError('اكتب اسم البنك بالعربي');
      return;
    }
    const validAccounts = bankEditor.accounts.filter((a) => a.value.trim());
    if (validAccounts.length === 0) {
      setError('أضف حساب واحد على الأقل');
      return;
    }
    setSavingBank(true);
    setError(null);
    try {
      let bankId = bankEditor.id;
      if (bankId) {
        // Edit mode: update bank row + fully replace its accounts.
        // Diffing per-account is more code than value — the accounts
        // table is small (one bank has 1-5 rows) so wipe + reinsert.
        const { error: upErr } = await supabase
          .from('payment_methods')
          .update({
            label_ar: bankEditor.label_ar.trim(),
            label_en: bankEditor.label_en.trim(),
            logo_url: bankEditor.logo_url || null,
          })
          .eq('id', bankId);
        if (upErr) throw upErr;
        const { error: delErr } = await supabase
          .from('payment_methods')
          .delete()
          .eq('parent_id', bankId);
        if (delErr) throw delErr;
      } else {
        // Create mode: insert bank first, we'll attach accounts to it.
        const bankSort = rows.filter((r) => r.section === 'bank' && r.kind === 'bank').length + 1;
        const { data: bankRow, error: bankErr } = await supabase
          .from('payment_methods')
          .insert({
            section: 'bank',
            kind: 'bank',
            label_ar: bankEditor.label_ar.trim(),
            label_en: bankEditor.label_en.trim(),
            logo_url: bankEditor.logo_url || null,
            sort_order: bankSort,
            active: true,
          })
          .select()
          .single();
        if (bankErr) throw bankErr;
        bankId = (bankRow as { id: string }).id;
      }

      const accountRows = validAccounts.map((a, i) => ({
        section: 'bank',
        kind: 'account',
        label_ar: `حساب ${a.currency}`,
        label_en: `${a.currency} account`,
        currency: a.currency,
        value: a.value.trim(),
        parent_id: bankId!,
        sort_order: i,
        active: true,
      }));
      const { error: acctErr } = await supabase.from('payment_methods').insert(accountRows);
      if (acctErr) throw acctErr;

      setBankEditor(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingBank(false);
    }
  };

  const removeBank = async (bank: Row) => {
    if (!(await confirm({
      title: `احذف البنك "${bank.label_ar}" وكل حساباته؟`,
      confirmText: 'احذف',
      tone: 'danger',
    }))) return;
    // FK is ON DELETE CASCADE on parent_id, but delete accounts
    // explicitly first for the audit trail.
    await supabase.from('payment_methods').delete().eq('parent_id', bank.id);
    const { error: err } = await supabase.from('payment_methods').delete().eq('id', bank.id);
    if (err) setError(err.message);
    else await load();
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setError(null);
    const payload = {
      ...editing,
      note_ar: editing.note_ar || null,
      note_en: editing.note_en || null,
      value: editing.value || null,
      currency: editing.currency || null,
      icon: editing.icon || null,
      logo_url: editing.logo_url || null,
      tint: editing.tint || null,
    };
    const { error: err } = creating
      ? await supabase.from('payment_methods').insert(payload)
      : await supabase.from('payment_methods').update(payload).eq('id', editing.id!);
    if (err) return setError(err.message);
    setEditing(null);
    setCreating(false);
    await load();
  };

  const remove = async (id: string) => {
    if (!(await confirm({
      title: 'حذف الطريقة دي؟',
      confirmText: 'احذف',
      tone: 'danger',
    }))) return;
    const { error: err } = await supabase.from('payment_methods').delete().eq('id', id);
    if (err) setError(err.message);
    else await load();
  };

  const bySection = (s: Section) =>
    rows.filter((r) => r.section === s).sort((a, b) => a.sort_order - b.sort_order);

  const parentLabel = (id: string | null) => {
    if (!id) return '—';
    return rows.find((r) => r.id === id)?.label_ar ?? id;
  };

  // Grouped banks view: each bank card lists its accounts nested,
  // with a "+ حساب" button that opens the modal pre-filled for that
  // bank. Cleaner than the flat table for the bank section.
  const bankGroup = () => {
    const banks = bySection('bank').filter((r) => r.kind === 'bank');
    if (banks.length === 0) return <div className="empty">مفيش بنوك</div>;
    return banks.map((b) => {
      const accts = bySection('bank')
        .filter((a) => a.parent_id === b.id)
        .sort((x, y) => x.sort_order - y.sort_order);
      return (
        <div key={b.id} className="card" style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {b.logo_url ? (
              <img src={b.logo_url} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'contain', background: '#f2f2f2' }} />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 8, background: '#f2f2f2', color: 'var(--text-secondary)', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                مفيش لوجو
              </div>
            )}
            <div style={{ flex: 1 }}>
              <strong>{b.label_ar}</strong>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{b.label_en}</div>
            </div>
            <button className="btn btn-small btn-secondary" onClick={() => startEditBank(b)}>تعديل</button>
            <button className="btn btn-small btn-danger" onClick={() => void removeBank(b)}>حذف</button>
          </div>
          {accts.length > 0 && (
            <table className="table" style={{ marginTop: 10 }}>
              <thead><tr><th style={{ width: 100 }}>العملة</th><th>رقم الحساب</th></tr></thead>
              <tbody>
                {accts.map((a) => (
                  <tr key={a.id}>
                    <td><span className="badge">{a.currency}</span></td>
                    <td style={{ direction: 'ltr', fontFamily: 'monospace' }}>{a.value ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    });
  };

  const sectionCard = (s: Section) => (
    <div key={s} className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <h3 style={{ margin: 0 }}>{SECTION_LABEL[s]}</h3>
        {s === 'bank' ? (
          <button className="btn btn-small" onClick={startNewBank}>+ إضافة بنك جديد</button>
        ) : (
          <button
            className="btn btn-small"
            onClick={() => {
              setCreating(true);
              setEditing({ ...emptyDraft, section: s, kind: s === 'delivery' ? 'call_center' : 'wallet' });
            }}
          >
            + إضافة
          </button>
        )}
      </div>
      {s === 'bank' ? (
        bankGroup()
      ) : bySection(s).filter((r) => r.section === s).length === 0 ? (
        <div className="empty">مفيش عناصر</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>الاسم</th>
              <th>{s === 'delivery' ? 'رقم التليفون' : 'الكود'}</th>
              <th>الترتيب</th>
              <th>ظاهر؟</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {bySection(s).map((r) => (
              <tr key={r.id}>
                <td style={{ verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {r.logo_url && <img src={r.logo_url} alt="" style={{ width: 32, height: 32, borderRadius: 6, objectFit: 'contain', background: '#f2f2f2' }} />}
                    <div>
                      <div>{r.label_ar}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.label_en}</div>
                      {r.note_ar && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{r.note_ar}</div>}
                    </div>
                  </div>
                </td>
                <td style={{ direction: 'ltr', fontFamily: 'monospace', verticalAlign: 'middle' }}>{r.value ?? '—'}</td>
                <td style={{ verticalAlign: 'middle' }}>{r.sort_order}</td>
                <td style={{ verticalAlign: 'middle' }}>
                  <span className={`badge ${r.active ? 'badge-success' : 'badge-muted'}`}>
                    {r.active ? 'نعم' : 'لا'}
                  </span>
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-small btn-secondary" onClick={() => { setCreating(false); setEditing({ ...r }); }}>تعديل</button>
                    <button className="btn btn-small btn-danger" onClick={() => void remove(r.id)}>حذف</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow="طرق التبرع"
        title="إدارة كل الطرق"
        subtitle="اللي بيظهر في تاب طرق التبرع على الموبايل — مندوب + إلكتروني + بنوك (كل بنك + حساباته)"
      />
      {error && <div className="error-inline">{error}</div>}

      {/* Tab selection now lives in the sidebar wrapper (URL-driven);
          this file only renders the one section that matches the
          route. */}

      {loading ? (
        <div className="empty">جاري التحميل...</div>
      ) : (
        sectionCard(tab)
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{creating ? 'طريقة جديدة' : `تعديل: ${editing.label_ar}`}</h2>
              <button className="btn btn-small btn-secondary" onClick={() => setEditing(null)}>إغلاق</button>
            </div>
            <form onSubmit={save}>
              {/* Per-section forms. The admin never sees "kind" or
                  raw MCI icon names — everything is spoken in the
                  vocabulary of the section: delivery = phone number,
                  electronic = company + code, bank = name + account
                  numbers per currency. */}

              {creating && (
                <div className="field">
                  <label>القسم</label>
                  <select value={editing.section} onChange={(e) => {
                    const section = e.target.value as Section;
                    const kind = section === 'delivery' ? 'call_center'
                      : section === 'electronic' ? 'wallet'
                      : 'bank';
                    setEditing({ ...editing, section, kind, parent_id: null });
                  }}>
                    <option value="delivery">مندوب (رقم اتصال)</option>
                    <option value="electronic">إلكتروني (شركة/محفظة/كود)</option>
                    <option value="bank">بنك (اسم البنك + حساباته)</option>
                  </select>
                </div>
              )}

              {/* ==== Delivery: phone number + optional icon ==== */}
              {editing.section === 'delivery' && (
                <>
                  <div className="row">
                    <div className="field">
                      <label>الاسم (عربي)</label>
                      <input required value={editing.label_ar} onChange={(e) => setEditing({ ...editing, label_ar: e.target.value })} placeholder="مثلاً: مركز الاتصال" />
                    </div>
                    <div className="field">
                      <label>Name (English)</label>
                      <input value={editing.label_en} onChange={(e) => setEditing({ ...editing, label_en: e.target.value })} placeholder="Call Center" />
                    </div>
                  </div>
                  <div className="field">
                    <label>رقم التليفون</label>
                    <input required value={editing.value ?? ''} onChange={(e) => setEditing({ ...editing, value: e.target.value })} placeholder="19340" style={{ direction: 'ltr' }} />
                  </div>
                </>
              )}

              {/* ==== Electronic: company + optional description/code ==== */}
              {editing.section === 'electronic' && (
                <>
                  <div className="row">
                    <div className="field">
                      <label>اسم الشركة (عربي)</label>
                      <input required value={editing.label_ar} onChange={(e) => setEditing({ ...editing, label_ar: e.target.value })} placeholder="مثلاً: فوري" />
                    </div>
                    <div className="field">
                      <label>Company (English)</label>
                      <input value={editing.label_en} onChange={(e) => setEditing({ ...editing, label_en: e.target.value })} placeholder="Fawry" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="field">
                      <label>وصف يظهر جوّه المربع فوق الكود (اختياري)</label>
                      <input value={editing.note_ar ?? ''} onChange={(e) => setEditing({ ...editing, note_ar: e.target.value })} placeholder="اتصالات كاش / أورانج كاش..." />
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        النص ده بيظهر جوّه كارت الكود فوق الرقم — مافيش وصف تاني فوق الكارت.
                      </div>
                    </div>
                    <div className="field">
                      <label>Caption inside the box, above the code (optional)</label>
                      <input value={editing.note_en ?? ''} onChange={(e) => setEditing({ ...editing, note_en: e.target.value })} placeholder="Vodafone Cash / Orange Cash..." />
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                        Shown inside the code card above the number — nothing appears above the card.
                      </div>
                    </div>
                  </div>
                  <div className="field">
                    <label>الكود اللي بيتنسخ (اختياري)</label>
                    <input value={editing.value ?? ''} onChange={(e) => setEditing({ ...editing, value: e.target.value })} placeholder="9200" style={{ direction: 'ltr' }} />
                  </div>
                </>
              )}

              {/* ==== Bank (kind=bank): bank name + optional icon ==== */}
              {editing.section === 'bank' && editing.kind === 'bank' && (
                <>
                  <div className="row">
                    <div className="field">
                      <label>اسم البنك (عربي)</label>
                      <input required value={editing.label_ar} onChange={(e) => setEditing({ ...editing, label_ar: e.target.value })} placeholder="بنك CIB" />
                    </div>
                    <div className="field">
                      <label>Short code / English</label>
                      <input value={editing.label_en} onChange={(e) => setEditing({ ...editing, label_en: e.target.value })} placeholder="CIB" style={{ direction: 'ltr' }} />
                    </div>
                  </div>
                </>
              )}

              {/* ==== Bank (kind=account): account details ==== */}
              {editing.section === 'bank' && editing.kind === 'account' && (
                <>
                  <div className="field">
                    <label>البنك اللي الحساب تابع له</label>
                    <select required value={editing.parent_id ?? ''} onChange={(e) => setEditing({ ...editing, parent_id: e.target.value || null })}>
                      <option value="">— اختر بنك —</option>
                      {rows.filter((r) => r.section === 'bank' && r.kind === 'bank').map((r) => (
                        <option key={r.id} value={r.id}>{r.label_ar}</option>
                      ))}
                    </select>
                  </div>
                  <div className="row">
                    <div className="field">
                      <label>العملة</label>
                      <select required value={editing.currency ?? ''} onChange={(e) => setEditing({ ...editing, currency: e.target.value || null })}>
                        <option value="">— اختر —</option>
                        <option value="EGP">جنيه مصري (EGP)</option>
                        <option value="USD">دولار أمريكي (USD)</option>
                        <option value="EUR">يورو (EUR)</option>
                      </select>
                    </div>
                    <div className="field">
                      <label>رقم الحساب</label>
                      <input required value={editing.value ?? ''} onChange={(e) => setEditing({ ...editing, value: e.target.value })} style={{ direction: 'ltr' }} />
                    </div>
                  </div>
                  <div className="row">
                    <div className="field">
                      <label>الاسم بالعربي (اختياري)</label>
                      <input value={editing.label_ar} onChange={(e) => setEditing({ ...editing, label_ar: e.target.value })} placeholder="حساب جنيه" />
                    </div>
                    <div className="field">
                      <label>Name (English)</label>
                      <input value={editing.label_en} onChange={(e) => setEditing({ ...editing, label_en: e.target.value })} placeholder="EGP account" />
                    </div>
                  </div>
                </>
              )}

              {/* ==== Optional icon / logo — applies to every kind ==== */}
              {!(editing.section === 'bank' && editing.kind === 'account') && (
                <div className="field">
                  <label>أيقونة / لوجو (اختياري — لو ما رفعتيش هيتحط أيقونة افتراضية)</label>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setUploadingLogo(true); setError(null);
                      try {
                        const url = await uploadPaymentIcon(f);
                        setEditing({ ...editing, logo_url: url });
                      } catch (err) {
                        setError(err instanceof Error ? err.message : String(err));
                      } finally {
                        setUploadingLogo(false);
                        if (logoInputRef.current) logoInputRef.current.value = '';
                      }
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {editing.logo_url ? (
                      <img src={editing.logo_url} alt="" style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'contain', background: '#f2f2f2' }} />
                    ) : (
                      <div style={{ width: 56, height: 56, borderRadius: 10, background: '#f2f2f2', color: 'var(--text-secondary)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        لا يوجد
                      </div>
                    )}
                    <button type="button" className="btn btn-small btn-secondary" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo}>
                      {uploadingLogo ? 'جاري الرفع...' : editing.logo_url ? 'تغيير' : 'رفع أيقونة/لوجو'}
                    </button>
                    {editing.logo_url && (
                      <button type="button" className="btn btn-small btn-danger" onClick={() => setEditing({ ...editing, logo_url: '' })}>مسح</button>
                    )}
                  </div>
                </div>
              )}

              <div className="row">
                <div className="field"><label>ترتيب العرض</label><input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} /></div>
                <div className="field">
                  <label>الحالة</label>
                  <select value={editing.active ? 'yes' : 'no'} onChange={(e) => setEditing({ ...editing, active: e.target.value === 'yes' })}>
                    <option value="yes">ظاهرة</option>
                    <option value="no">مخفية</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>إلغاء</button>
                <button type="submit" className="btn">{creating ? 'نشر' : 'حفظ'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==== One-shot Add Bank wizard ==== */}
      {bankEditor && (
        <div className="modal-backdrop" onClick={() => !savingBank && setBankEditor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{bankEditor.id ? `تعديل بنك: ${bankEditor.label_ar || ''}` : 'إضافة بنك جديد'}</h2>
              <button className="btn btn-small btn-secondary" onClick={() => !savingBank && setBankEditor(null)}>إغلاق</button>
            </div>
            <div style={{ padding: 4 }}>
              <div className="row">
                <div className="field">
                  <label>اسم البنك (عربي) *</label>
                  <input
                    value={bankEditor.label_ar}
                    onChange={(e) => setBankEditor({ ...bankEditor, label_ar: e.target.value })}
                    placeholder="مثلاً: بنك CIB"
                  />
                </div>
                <div className="field">
                  <label>Bank name (English)</label>
                  <input
                    value={bankEditor.label_en}
                    onChange={(e) => setBankEditor({ ...bankEditor, label_en: e.target.value })}
                    placeholder="CIB"
                    style={{ direction: 'ltr' }}
                  />
                </div>
              </div>

              <div className="field">
                <label>لوجو البنك (اختياري)</label>
                <input
                  ref={bankLogoInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setUploadingBankLogo(true);
                    setError(null);
                    try {
                      const url = await uploadPaymentIcon(f);
                      setBankEditor((cur) => (cur ? { ...cur, logo_url: url } : cur));
                    } catch (err) {
                      setError(err instanceof Error ? err.message : String(err));
                    } finally {
                      setUploadingBankLogo(false);
                      if (bankLogoInputRef.current) bankLogoInputRef.current.value = '';
                    }
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {bankEditor.logo_url ? (
                    <img src={bankEditor.logo_url} alt="" style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'contain', background: '#f2f2f2' }} />
                  ) : (
                    <div style={{ width: 64, height: 64, borderRadius: 10, background: '#f2f2f2', color: 'var(--text-secondary)', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      لا يوجد
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-small btn-secondary"
                    onClick={() => bankLogoInputRef.current?.click()}
                    disabled={uploadingBankLogo}
                  >
                    {uploadingBankLogo ? 'جاري الرفع...' : bankEditor.logo_url ? 'تغيير' : 'رفع لوجو'}
                  </button>
                  {bankEditor.logo_url && (
                    <button
                      type="button"
                      className="btn btn-small btn-danger"
                      onClick={() => setBankEditor({ ...bankEditor, logo_url: '' })}
                    >
                      مسح
                    </button>
                  )}
                </div>
              </div>

              <div style={{ marginTop: 14, marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <strong>الحسابات البنكيه</strong>
                <button
                  type="button"
                  className="btn btn-small"
                  onClick={() =>
                    setBankEditor({
                      ...bankEditor,
                      accounts: [...bankEditor.accounts, { currency: 'EGP', value: '' }],
                    })
                  }
                >
                  + أضف حساب
                </button>
              </div>

              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 140 }}>العمله</th>
                    <th>رقم الحساب</th>
                    <th style={{ width: 80 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {bankEditor.accounts.map((a, i) => (
                    <tr key={i}>
                      <td>
                        <select
                          value={a.currency}
                          onChange={(e) => {
                            const accounts = [...bankEditor.accounts];
                            accounts[i] = { ...accounts[i], currency: e.target.value as BankEditorAccount['currency'] };
                            setBankEditor({ ...bankEditor, accounts });
                          }}
                          style={{ width: '100%' }}
                        >
                          <option value="EGP">جنيه (EGP)</option>
                          <option value="USD">دولار (USD)</option>
                          <option value="EUR">يورو (EUR)</option>
                        </select>
                      </td>
                      <td>
                        <input
                          value={a.value}
                          onChange={(e) => {
                            const accounts = [...bankEditor.accounts];
                            accounts[i] = { ...accounts[i], value: e.target.value };
                            setBankEditor({ ...bankEditor, accounts });
                          }}
                          placeholder="1015996610010201"
                          style={{ direction: 'ltr', width: '100%' }}
                        />
                      </td>
                      <td>
                        {bankEditor.accounts.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-small btn-danger"
                            onClick={() => {
                              const accounts = bankEditor.accounts.filter((_, j) => j !== i);
                              setBankEditor({ ...bankEditor, accounts });
                            }}
                          >
                            شيل
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => !savingBank && setBankEditor(null)}>إلغاء</button>
                <button type="button" className="btn" onClick={() => void saveBankEditor()} disabled={savingBank}>
                  {savingBank ? 'جاري الحفظ...' : 'حفظ البنك وحساباته'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
